import centers from "../data/Centers.json";
import React, { useEffect, useState } from "react";
import "../styles/Centers.css";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Link } from "react-router-dom";

function Centers() {
  const [data, setData] = useState(null);
  const [filterLetter, setFilterLetter] = useState("A");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    try {
      setData(centers);
      setLoading(false);
    } catch (err) {
      console.error("Error loading JSON:", err);
      setError("Failed to load center data. Please try again later.");
      setLoading(false);
    }
  }, []);

  if (loading) return <p>Loading...</p>;
  if (error) return <p style={{ color: "red" }}>{error}</p>;

  const filteredCountries = data.Countries.filter((country) =>
    country.Country.startsWith(filterLetter),
  );

  return (
    <div>
      <title>Centers for Pulmonary Rehab</title>
      <HelmetProvider>
        <Helmet>
          <title>
            Center for Noninvasive Mechanical Ventilation & Pulmonary Rehab
          </title>
          <meta
            name="description"
            content="Learn about our Center for Noninvasive Mechanical Ventilation Alternatives and Pulmonary Rehabilitation—established in 1992 to serve patients with neuromuscular weakness and respiratory impairment. Find information about trained individuals all over the world that specialize in Noninvasive Respiratory Management of Neuromuscular diseases."
          />
          <meta
            name="keywords"
            content="Rehabilitation, Mechanical Ventilation, Noninvasive Ventilation, VVS, NIV, Pulmonary Rehabilitation, Neuromuscular Disease, Respiratory Therapy, Cough-Assist, pulmonary rehab specialists, doctor, trained individuals"
          />
          <meta
            property="og:title"
            content="Noninvasive Ventilation & Pulmonary Rehab Centers"
          />
          <meta
            property="og:image"
            content="https://doctorbach.com/favicon.png"
          />
          <meta
            property="og:description"
            content="Dedicated since 1992 to noninvasive care: orthopedic approaches, muscle aids, anesthesia protocols, and tracheostomy avoidance."
          />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://doctorbach.com/ourcenters" />
        </Helmet>
      </HelmetProvider>

      <div className="about-container">
        <div className="center-title">
          <h3>Centers for NVS and MIE</h3>
        </div>

        {/* Introduction Section */}
        <div className="cent-container">
          <div className="about-introduction">
            <p>
              Pioneered at the Center for NVS and MIE in the United States, the
              Center for Noninvasive Mechanical Ventilation Alternatives and
              Pulmonary Rehabilitation was established in 1992. In consists of
              physicians and other health care professionals dedicated to the
              care of patients with neuromuscular weakness and respiratory
              impairment from any cause.
            </p>
            <div className="centers-pro">
              <h4 style={{ color: "#4a4a4a" }}>
                The center's physicians have:
              </h4>
              <ul>
                <li>
                  Developed orthopedic approaches that prevent musculotendinous
                  contractures and prolong brace-free ambulation for some people
                  with weak leg muscles.
                </li>
                <li>
                  Pioneered NVS and MIE, inspiratory and expiratory muscle aids,
                  that prevent respiratory complications of neuromuscular
                  weakness, and very weak or overworked breathing muscles.
                </li>
                <li>
                  Were first to describe cardioprotective medications in 1999,
                  that have reversed the cardiac stress associated with the
                  cardiomyopathies of myopathic diseases.
                </li>
                <li>
                  Were first to describe robotics for upper limb function for
                  people with only finger movements in 1987.
                </li>
                <li>
                  {" "}
                  Developed approaches that avoid the use of general anesthesia
                  for placing stomach tubes.
                </li>
              </ul>
            </div>
            <p>
              Our physicians and surgeons use inspiratory and expiratory muscle
              aids (NVS and MIE) such that surgery is performed on patients with
              little or no respiratory muscle function with minimal risk of
              respiratory complications and no need to resort to tracheostomy.
              Despite 8 hour scoliosis surgeries and other surgeries for people
              with vital capacities as low as 80 ml, we have never had a
              pneumonia or other respiratory complication because of use of MIE
              and NVS.
            </p>
            <p>
              Our respiratory therapists are specifically trained in noninvasive
              methods to avoid invasive airway tubes. Our center has removed
              over 100 tracheostomy tubes from ventilator users with no ability
              to breathe on their own and taught many of them, even those with
              little or no respiratory muscle function, how to breathe without
              ventilators by only using their tongues (
              <Link
                to="/resources/as"
                style={{ textDecoration: "none", color: "darkblue" }}
              >
                glossopharyngeal breathing
              </Link>
              ). See our books on the{" "}
              <Link
                to="/"
                style={{ textDecoration: "none", color: "darkblue" }}
              >
                home page
              </Link>{" "}
              to learn more about our centers physicians and supporting staff.
            </p>
            <h4 className="link-center" style={{ color: "#4a4a4a" }}>
              To read more about our center Physicians and Supporting Staff in
              Newark, New Jersey,&nbsp;
              <Link
                to="/ourcenters/staff"
                style={{ textDecoration: "none", color: "darkblue" }}
              >
                click here.
              </Link>
            </h4>{" "}
            <h4 className="link-center" style={{ color: "#4a4a4a" }}>
              To read an introduction to our programs,&nbsp;
              <Link
                to="/ourcenters/programs"
                style={{ textDecoration: "none", color: "darkblue" }}
              >
                click here.
              </Link>
            </h4>
            <h4 className="link-center" style={{ color: "#4a4a4a" }}>
              For further information,&nbsp;
              <Link
                to="/ourcenters/furtherinformation"
                style={{ textDecoration: "none", color: "darkblue" }}
              >
                click here.
              </Link>
            </h4>{" "}
            <p
              className="centers-intro-copy"
              style={{ paddingTop: "16px", color: "#4a4a4a" }}
            >
              We operate centers worldwide dedicated to supporting individuals
              with neuromuscular diseases. Below, you’ll find information about
              qualified medical professionals available across various countries
              all over the world.
            </p>
          </div>
        </div>
      </div>

      <div className="alphabet-filter">
        {"ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").map((letter) => {
          const hasMatch = data.Countries.some(
            (country) => country.Country[0].toUpperCase() === letter,
          );

          return (
            <button
              key={letter}
              onClick={hasMatch ? () => setFilterLetter(letter) : undefined}
              disabled={!hasMatch}
              style={{
                margin: "2px",
                backgroundColor:
                  filterLetter === letter
                    ? "#015798"
                    : hasMatch
                      ? "#ccc"
                      : "#eee",
                color: hasMatch ? "white" : "#888",
                border: "none",
                padding: "5px 10px",
                cursor: hasMatch ? "pointer" : "not-allowed",
              }}
            >
              {letter}
            </button>
          );
        })}
      </div>

      <div className="country-centers">
        {filteredCountries.map((country, idx) => (
          <div key={idx} className="country-section">
            <div className="country-title">
              <h2>Centers in {country.Country}</h2>
            </div>
            {country.data.map((cityData, cityIdx) => (
              <div key={cityIdx} className="city-section">
                <h3>Our Centers in {cityData.city}</h3>
                <div className="center-cards">
                  {cityData.items.map((center, centerIdx) => (
                    <div key={centerIdx} className="center-card">
                      {(center.title || center.name) && (
                        <h3>
                          {center.title} {center.name}
                        </h3>
                      )}
                      {center.organization && (
                        <p>
                          <strong>Organization:</strong> {center.organization}
                        </p>
                      )}
                      {center.hospital && (
                        <p>
                          <strong>Hospital/Clinic:</strong> {center.hospital}
                        </p>
                      )}

                      {center.location && (
                        <p>
                          <strong>Location:</strong> {center.location}
                        </p>
                      )}

                      {center.services && (
                        <p>
                          <strong>Services:</strong> {center.services}
                        </p>
                      )}

                      {center.website && (
                        <p>
                          <strong>Website:</strong> {center.website}
                        </p>
                      )}

                      {center.contact?.main_email && (
                        <p>
                          <strong>Email:</strong> {center.contact.main_email}
                        </p>
                      )}
                      {center.contact?.personal_email && (
                        <p>
                          <strong>Personal Email:</strong>{" "}
                          {center.contact.personal_email}
                        </p>
                      )}
                      {center.contact?.main_phone && (
                        <p>
                          <strong>Phone:</strong> {center.contact.main_phone}
                        </p>
                      )}
                      {center.contact?.personal_phone && (
                        <p>
                          <strong>Personal Phone:</strong>{" "}
                          {center.contact.personal_phone}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default Centers;
